import {useObservable} from '@ngneat/react-rxjs';
import {useTranslation} from 'react-i18next';
import {statisticsQuery} from '@store/statistics';
import {Colors} from '@constants/colors.constants';
import {alpha, Box, CircularProgress, Stack, Typography} from '@mui/material';
import {Chart} from 'react-chartjs-2';
import {NumberExt} from '@utils/extensions/number.extension';
import React from 'react';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ScatterController,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ScatterController, BarController, BarElement);

const TurnoverChargesServiceKindChart = () => {
  const {t} = useTranslation();

  const [{turnoverAndChargesByServiceKind, loading}] = useObservable(statisticsQuery.turnoverChargesServiceKind$);

  const totalBuy = (turnoverAndChargesByServiceKind?.reduce((acc, item) => acc + item.charges, 0) || 1) / 100;
  const totalSell = (turnoverAndChargesByServiceKind?.reduce((acc, item) => acc + item.turnover, 0) || 1) / 100;


  return (
    <Stack height="360px" bgcolor={alpha(Colors.primary, 0.2)} borderRadius="4px" px="3px" py="20px">
      <Typography fontSize={18} fontWeight="700" ml="17px" mb="20px">
        {t('dashboard.statistics.finance.turnoverAndChargesByServiceKind.title')}
      </Typography>
      {loading && <CircularProgress size={30} style={{alignSelf: 'center'}}/>}
      {!loading && (
        <Box flex={1} mb="15px" width="100%">
          <Chart height="100%"
                 type="bar"
                 options={{
                   maintainAspectRatio: false,
                   responsive: true,
                   plugins: {
                     legend: {display: false},
                     title: {display: false},
                     datalabels: {display: false},
                     tooltip: {
                       backgroundColor: Colors.background,
                       displayColors: false,
                       cornerRadius: 4,
                       padding: 5,
                       xAlign: 'left',
                       yAlign: 'bottom',
                       caretSize: 0,
                       caretPadding: 10,
                       bodyColor: Colors.text,
                       bodyFont: {
                         family: 'CircularStd, sans-serif',
                         size: 14,
                         weight: '500',
                       },
                       callbacks: {
                         title: function () {
                           return '';
                         },
                         label: function (context: TooltipItem<'bar' | 'scatter'>) {
                           return context.dataset.type === 'scatter' ? `Marge : ${Math.round(context.parsed.y * 100) / 100} %` : `${NumberExt.toCurrency(context.parsed.y)} - ${context.datasetIndex == 1 ? Math.round(context.parsed.y / totalBuy * 10000) / 100 : Math.round(context.parsed.y / totalSell * 10000) / 100}%`;
                         },
                       },
                     },
                   },
                   scales: {
                     x: {
                       grid: {
                         display: false,
                         drawBorder: false,
                         drawTicks: false,
                       },
                       ticks: {
                         color: Colors.text,
                         font: {
                           family: 'CircularStd, sans-serif',
                           size: 10,
                         },
                         backdropPadding: 0,
                         padding: 10,
                       },
                     },
                     y: {
                       beginAtZero: true,
                       grid: {
                         borderDash: [2],
                         color: alpha(Colors.chartBorder, 0.5),
                         drawBorder: false,
                         drawTicks: false,
                       },
                       ticks: {
                         count: 6,
                         color: Colors.text,
                         font: {
                           family: 'CircularStd, sans-serif',
                           size: 10,
                         },
                         backdropPadding: 0,
                         padding: 10,
                       },
                     },
                     yAxis: {
                       beginAtZero: true,
                       position: 'right',
                       title: {
                         text: 'Marge',
                         display: true
                       },
                       grid: {
                         borderDash: [2],
                         color: alpha(Colors.chartBorder, 0.5),
                         drawBorder: false,
                         drawTicks: false,
                       },
                       ticks: {
                         callback: (value) => {
                           return `${value}%`;
                         },
                         count: 6,
                         color: Colors.text,
                         font: {
                           family: 'CircularStd, sans-serif',
                           size: 10,
                         },
                         backdropPadding: 0,
                         padding: 10,
                       },
                     },
                   },
                 }}

                 data={{
                   labels: turnoverAndChargesByServiceKind?.map((item) => t(`global.serviceKindType.${item.serviceKindType.toLowerCase()}`)),
                   datasets: [
                     {
                       type: 'scatter',
                       data: turnoverAndChargesByServiceKind?.map((item) => (item.turnover - item.charges) * 100 / item.charges),
                       borderColor: Colors.white,
                       backgroundColor: Colors.black,
                       pointRadius: 10,
                       pointHoverBorderColor: Colors.primary,
                       pointHoverBackgroundColor: Colors.white,
                       pointHoverRadius: 5,
                       pointHoverBorderWidth: 4,
                       borderWidth: 5,
                       yAxisID: 'yAxis',
                     },
                     {
                       data: turnoverAndChargesByServiceKind?.map((item) => item.charges / 100),
                       backgroundColor: Colors.warning,
                       borderColor: Colors.warning,
                       borderWidth: 1,
                     },
                     {
                       data: turnoverAndChargesByServiceKind?.map((item) => item.turnover / 100),
                       backgroundColor: Colors.blue,
                       borderColor: Colors.blue,
                       borderWidth: 1,
                     },
                   ]
                 }}/>
        </Box>
      )}
      <Stack
        bgcolor={Colors.background}
        borderRadius="4px"
        px="20px"
        py="10px"
        direction="row"
        alignItems="center"
        alignSelf="center"
        spacing={3}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.warning} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.purchaseInvoiceLines')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.blue} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.turnoverInvoiceLines')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default TurnoverChargesServiceKindChart;
